import { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import {
  MessageBox,
  speakMessage,
  Message,
  useGenerateMessage,
} from '@components'
import { useChatStore } from '@client/store'
import { memoryOptimizer } from '@client/utility'
import { Virtuoso } from 'react-virtuoso'

interface ChatMessagesProps {
  previous_message: Message[]
  setIsGenerating: (val: boolean) => void
  onGenerateImage: (msgId: number) => void
  onEditClick: (
    messageId: number,
    currentMessage: string,
    type: 'user' | 'char'
  ) => void
  onRemove: (msgId: number) => void
}

export function ChatMessages({
  previous_message,
  setIsGenerating,
  onGenerateImage,
  onEditClick,
  onRemove,
}: ChatMessagesProps) {
  const { character, messages: storeMessages, setMessages } = useChatStore()
  const { generateMessage } = useGenerateMessage()
  const [isStreaming, setIsStreaming] = useState(false)
  const virtuosoRef = useRef<any>(null)

  useEffect(() => {
    const isSame =
      previous_message.length === storeMessages.length &&
      previous_message.every((m, i) => m.id === storeMessages[i]?.id)
      
    if (!isSame) setMessages(previous_message)
  }, [previous_message, storeMessages, setMessages])

  useEffect(() => {
    if (virtuosoRef.current && storeMessages.length) {
      virtuosoRef.current.scrollToIndex({
        index: storeMessages.length - 1,
        align: 'end',
        behavior: 'auto',
      })
    }
  }, [storeMessages])

  const handleSpeak = async (text: string, gender?: string) => {
    if (!text) return
    setIsGenerating(true)
    try {
      await speakMessage(text, gender || 'neutral', () =>
        setIsGenerating(false)
      )
    } catch {
      setIsGenerating(false)
    }
  }

  const handleCopy = (text: string) =>
    navigator.clipboard.writeText(text || '')

  const handleRegenerate = async (msg: Message) => {
    if (!msg.text) return
    const messageID = msg.ref_id || msg.id
    if (!messageID || messageID === -1) return
    setIsStreaming(true)
    setIsGenerating(true)
    try {
      const isFirstMessage = msg.is_first === true
      
      await generateMessage(
        msg.text,
        msg.chat_uuid,
        0,
        'Regenerate',
        messageID,
        isFirstMessage
      )
    } catch (err) {
      console.error(err)
    } finally {
      setIsStreaming(false)
      setIsGenerating(false)
      memoryOptimizer.optimize()
    }
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: '100%', width: '100%', overflowX: 'hidden' }}
        data={storeMessages}
        itemContent={(index, msg) => {
          const isLastMessage = index === storeMessages.length - 1
          return (
            <Box
              sx={{
                width: '100%',
                maxWidth: 960,
                mx: 'auto',
                px: 2,
                py: 1,
              }}
            >
              <MessageBox
                key={`${msg.type}-${msg.id}-${index}`}
                index={index}
                isGenerating={isStreaming}
                isLastMessage={isLastMessage}
                handleSpeak={() => handleSpeak(msg.text, character?.gender)}
                onGenerateImage={onGenerateImage}
                onEditClick={onEditClick}
                onRemove={() => onRemove(msg.id)}
                onRegenerate={() => handleRegenerate(msg)}
                handleCopy={() => handleCopy(msg.text || '')}
              />
            </Box>
          )
        }}
        followOutput="auto"
      />
    </Box>
  )
}